import type { Tokens } from '@grammarly/design-system'
import { Flex, Icon, IconButton, InterfaceInfoIcon, Text } from '@grammarly/design-system'
import classes from './ProgressBar.module.scss'
import '../utils/allowCssVariables'
import { StickerPeelIcon } from '../icons/StickerPeelIcon'
import classNames from 'classnames'

export interface ProgressBarTheme {
  title?: string
  counter?: string
  maxCount?: string
}

export interface ProgressBarProps {
  /**
   * String labelling the entire gauge, at the top
   */
  title: string

  /**
   * Color for the score portion of the bar
   */
  color: Tokens.CoreColors

  /**
   * The value for the bar.
   * - If 'maxValue' is not provided, expects a value between 0-1, representing a
   *   percentage value
   * - If 'maxValue' is provided, expects a value that is <= 'maxValue'. The
   *   value of the bar is calculated as value/maxValue
   */
  value: number

  /**
   * The upper bound for the value. If provided, in addition to changing
   * how value is interpreted, we also render the score as a fraction rather
   * than percent
   */
  maxValue?: number

  /**
   * The label for the value. If provided, it will be displayed instead of the
   * value itself
   */
  valueLabel?: string

  /**
   * The explanation for the value. If provided, it will be displayed as a tooltip of the info button.
   */
  explanation?: string

  /**
   * If locked, the actual value and progress is hidden
   */
  locked?: {
    reason: string
  }

  /**
   * The theme for the progress bar
   */
  theme?: ProgressBarTheme
}

const percentFormat = Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
})

/**
 * Component for rendering the gauge indicator used for reporting in agent panels
 */
export const ProgressBar = ({
  title,
  value,
  color,
  maxValue,
  valueLabel,
  explanation,
  locked,
  theme,
}: ProgressBarProps) => {
  const barProgressValue = locked ? 0 : maxValue !== undefined ? value / maxValue : value

  const lockedUI = locked ? (
    <Icon
      icon={StickerPeelIcon}
      accessibilityLabel={locked.reason}
      size="large"
      className={classes['locked-icon']}
    />
  ) : null

  return (
    <div
      className={classes.root}
      style={{
        '--bar-progress': `${barProgressValue * 100}%`,
        '--bar-color': `var(--${color})`,
      }}
    >
      <Flex direction="row" justify="space-between" align="end">
        <Flex direction="row" gap={1} align="center">
          <Text
            className={classNames(classes.title, theme?.title)}
            as="span"
            variant="text-small"
            color="base-default"
          >
            {title}
          </Text>
          {explanation && (
            <IconButton
              icon={InterfaceInfoIcon}
              accessibilityLabel={explanation}
              variant="tertiary"
              size="small"
              className={classes['explanation']}
            />
          )}
        </Flex>
        {maxValue === undefined ? (
          lockedUI ? (
            lockedUI
          ) : (
            <Text as="span" variant="text-small" weight="medium" color="base-default">
              {valueLabel ?? percentFormat.format(value)}
            </Text>
          )
        ) : (
          <Flex align="end" gap={1}>
            {lockedUI ? (
              lockedUI
            ) : (
              <Text
                className={classNames(classes.counter, theme?.counter)}
                as="span"
                variant="text-small"
              >
                {valueLabel ?? value}
              </Text>
            )}
            <Text className={theme?.maxCount} as="span" variant="text-xsmall" color="base-subdued">
              / {maxValue}
            </Text>
          </Flex>
        )}
      </Flex>
      <div className={classes.bar} />
    </div>
  )
}
